/**
 * ============================================================================
 * PREISVERGLEICH.JSX v1.0 — Preisvergleich Overlay
 * ============================================================================
 *
 * Fullscreen-Overlay mit Preisvergleich-Ergebnissen vom N8N-Webhook.
 * Wird aus ListDetail.js aufgerufen.
 *
 * Views: Laden | Tabelle | Kombi
 * Sektionen: KPI-Bar, View-Toggle, Store-Cards, Auswertung, Nicht-Gefunden
 *
 * @version 1.0.0
 */

import React, { useState, useEffect } from 'react';
import { useToken } from '../../context/TokenContext';
import './Preisvergleich.css';

// ─── Konstanten ───────────────────────────────────────────────────────────────

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const MEDALS = ['🥇', '🥈', '🥉'];

// Entwicklungs-Mock — auf false setzen für Produktion
const USE_MOCK = false;
const MOCK_DATA = {
  success: true, mode: 'liste', artikelGesucht: 5, artikelGefunden: 4,
  stores: [
    {
      name: 'Rewe', key: 'rewe', rank: 1,
      articleCount: 4, totalArticles: 5, coverage: 80,
      totalPrice: 7.06, diffVsBest: 0,
      items: [
        { article: 'Milch', product: 'Ja! Milch 1l', brand: 'Ja!', price: 0.79, grundpreis: '0.79 €/l', rabattProzent: 0, isOffer: false, offerText: null, isCheapest: false },
        { article: 'Butter', product: 'Deutsche Markenbutter', brand: 'Ja!', price: 2.29, grundpreis: '9.16 €/kg', rabattProzent: 0, isOffer: false, offerText: null, isCheapest: false },
        { article: 'Tomaten', product: 'Rispen-Tomaten', brand: '', price: 1.49, grundpreis: '1.49 €/kg', rabattProzent: 0, isOffer: false, offerText: null, isCheapest: false },
        { article: 'Toast', product: 'Ja! Buttertoast', brand: 'Ja!', price: 1.09, grundpreis: '2.18 €/kg', rabattProzent: 0, isOffer: false, offerText: null, isCheapest: false }
      ]
    },
    {
      name: 'Lidl', key: 'lidl', rank: 2,
      articleCount: 3, totalArticles: 5, coverage: 60,
      totalPrice: 2.47, diffVsBest: 4.59,
      items: [
        { article: 'Butter', product: 'Milbona Markenbutter', brand: 'Milbona', price: 0.59, grundpreis: '2.36 €/kg', rabattProzent: 20, isOffer: true, offerText: '-20%', isCheapest: true },
        { article: 'Milch', product: 'Milbona Frischmilch', brand: 'Milbona', price: 0.59, grundpreis: '0.59 €/l', rabattProzent: 0, isOffer: false, offerText: null, isCheapest: true },
        { article: 'Toast', product: 'Grafschafter Toast', brand: '', price: 1.29, grundpreis: '2.58 €/kg', rabattProzent: 0, isOffer: false, offerText: null, isCheapest: false }
      ]
    }
  ],
  isSameStore: false,
  bestCoverage: { name: 'Rewe', key: 'rewe', totalPrice: 7.06, articleCount: 4, totalArticles: 5, coverage: 80 },
  bestPrice:    { name: 'Lidl', key: 'lidl', totalPrice: 2.47, articleCount: 3, totalArticles: 5, coverage: 60 },
  bestStore:    { name: 'Rewe', key: 'rewe', totalPrice: 7.06, articleCount: 4, totalArticles: 5, coverage: 80 },
  twoStoreCombo: { store1: 'Rewe', store1Key: 'rewe', store2: 'Lidl', store2Key: 'lidl', swapArticle: 'Butter', store1Remaining: 4.77, store1RemainingCount: 3, store2Price: 0.59, store1OriginalPrice: 2.29, totalPrice: 5.36, savings: 1.70 },
  notFound: ['Hackfleisch']
};

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

const fmt = (price) =>
  price != null ? Number(price).toFixed(2) + ' €' : '—';

// ─── Sub-Komponenten ──────────────────────────────────────────────────────────

const LadenView = ({ data, expandedStores, toggleStore }) => (
  <div className="pv-laden-list">
    {data.stores.map((store, idx) => {
      const isExpanded = expandedStores.has(store.key);
      const isBest = store.rank === 1;

      return (
        <div key={store.key} className={`pv-store-card${isBest ? ' pv-store-best' : ''}`}>
          <button
            className="pv-store-header"
            onClick={() => toggleStore(store.key)}
            aria-expanded={isExpanded}
          >
            <div className="pv-store-header-left">
              <span className="pv-medal">{MEDALS[idx] || String(idx + 1)}</span>
              <div>
                <div className="pv-store-name">{store.name}</div>
                <div className="pv-store-meta">{store.articleCount}/{store.totalArticles} Artikel · {store.coverage}%</div>
              </div>
            </div>
            <div className="pv-store-header-right">
              <div className="pv-price-label">GESAMTPREIS</div>
              <div className="pv-store-price">{fmt(store.totalPrice)}</div>
              {store.diffVsBest === 0
                ? <div className="pv-badge-best">✓ Günstigster</div>
                : <div className="pv-diff-label">+{fmt(store.diffVsBest)} vs. {data.bestStore?.name}</div>
              }
            </div>
            <span className="pv-chevron">{isExpanded ? '▲' : '▼'}</span>
          </button>

          <div className={`pv-store-items${isExpanded ? ' pv-store-items-open' : ''}`}>
            {store.items.map((item, i) => (
              <div key={i} className="pv-item">
                <div className="pv-item-left">
                  {item.isCheapest && <span className="pv-cheapest-dot" title="Günstigster Preis">●</span>}
                  <div>
                    <div className="pv-item-name">{item.article}</div>
                    {item.product !== item.article && (
                      <div className="pv-item-product">{item.product}</div>
                    )}
                    <div className="pv-item-grundpreis">{item.grundpreis}</div>
                  </div>
                </div>
                <div className="pv-item-right">
                  {item.isOffer && item.offerText && (
                    <span className="pv-offer-badge">{item.offerText}</span>
                  )}
                  <span className="pv-item-price">{fmt(item.price)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    })}
  </div>
);

const TabelleView = ({ data }) => {
  const articles = [...new Set(
    data.stores.flatMap(s => s.items.map(i => i.article))
  )].sort();

  const getPrice = (article, storeKey) => {
    const store = data.stores.find(s => s.key === storeKey);
    return store?.items.find(i => i.article === article)?.price ?? null;
  };

  return (
    <div className="pv-table-wrapper">
      <table className="pv-table">
        <thead>
          <tr>
            <th className="pv-th-sticky">Artikel</th>
            {data.stores.map(s => (
              <th key={s.key}>{MEDALS[s.rank - 1] || s.rank} {s.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {articles.map(article => {
            const prices = data.stores.map(s => getPrice(article, s.key));
            const validPrices = prices.filter(p => p != null);
            const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : null;

            return (
              <tr key={article}>
                <td className="pv-td-sticky">{article}</td>
                {prices.map((price, idx) => (
                  <td key={idx} className={price != null && price === minPrice ? 'pv-td-best' : ''}>
                    {price != null ? fmt(price) : '—'}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td className="pv-td-sticky pv-tfoot-label">Summe</td>
            {data.stores.map(s => (
              <td key={s.key} className={s.key === data.bestStore?.key ? 'pv-td-best pv-tfoot-value' : 'pv-tfoot-value'}>
                {fmt(s.totalPrice)}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const KombiView = ({ data }) => (
  <div className="pv-kombi-list">
    {data.stores.map((store, idx) => {
      const isBest = store.rank === 1;

      return (
        <div key={store.key} className={`pv-store-card${isBest ? ' pv-store-best' : ''}`}>
          <div className="pv-kombi-store-header">
            <span className="pv-medal">{MEDALS[idx] || String(idx + 1)}</span>
            <span className="pv-store-name">{store.name}</span>
            <span className="pv-store-price">{fmt(store.totalPrice)}</span>
          </div>

          <table className="pv-kombi-table">
            <thead>
              <tr>
                <th>Artikel</th>
                <th>Produkt</th>
                <th className="pv-th-grundpreis">Grundpreis</th>
                <th className="pv-th-preis">Preis</th>
              </tr>
            </thead>
            <tbody>
              {store.items.map((item, i) => (
                <tr key={i} className={item.isCheapest ? 'pv-kombi-cheapest' : ''}>
                  <td>{item.article}</td>
                  <td className="pv-kombi-product">{item.product}</td>
                  <td className="pv-text-muted">{item.grundpreis}</td>
                  <td className="pv-mono">{fmt(item.price)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className="pv-tfoot-label">Summe</td>
                <td className={`pv-mono${isBest ? ' pv-text-green' : ''}`}>
                  {fmt(store.totalPrice)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      );
    })}
  </div>
);

const Auswertung = ({ data }) => {
  const { bestCoverage, bestPrice, isSameStore, bestStore, twoStoreCombo } = data;
  const refStore = bestCoverage || bestStore;

  return (
    <div className="pv-auswertung">

      {/* ── Section A: Empfehlungen ── */}
      {isSameStore ? (
        /* Einzelkarte wenn bester Laden = günstigster */
        <div className="pv-best-card">
          <div className="pv-best-card-eyebrow">🏆 BESTER LADEN FÜR DICH</div>
          <div className="pv-best-card-body">
            <div className="pv-best-card-info">
              <div className="pv-best-store-name">{bestStore?.name}</div>
              <div className="pv-best-store-meta">
                {bestStore?.articleCount} von {bestStore?.totalArticles} Artikeln · {bestStore?.coverage}%
              </div>
              <div className="pv-best-checkmarks">
                <div>✓ Höchste Abdeckung</div>
                <div>✓ Günstigster Gesamtpreis</div>
                <div>✓ 1 Laden, alles erledigt</div>
              </div>
            </div>
            <div className="pv-best-price-box">
              <div className="pv-best-price-value">{fmt(bestStore?.totalPrice)}</div>
              <div className="pv-best-price-label">Gesamt</div>
            </div>
          </div>
        </div>
      ) : (
        /* Zwei Karten: Beste Verfügbarkeit + Günstigster */
        <div className="pv-rec-cards">
          {bestCoverage && (
            <div className="pv-best-card">
              <div className="pv-best-card-eyebrow">🛒 BESTE VERFÜGBARKEIT</div>
              <div className="pv-best-card-body">
                <div className="pv-best-card-info">
                  <div className="pv-best-store-name">{bestCoverage.name}</div>
                  <div className="pv-best-store-meta">
                    {bestCoverage.articleCount}/{bestCoverage.totalArticles} Artikel · {bestCoverage.coverage}%
                  </div>
                  <div className="pv-best-checkmarks">
                    <div>✓ Meiste Artikel auf deiner Liste</div>
                    <div>✓ 1 Laden, alles erledigt</div>
                  </div>
                </div>
                <div className="pv-best-price-box">
                  <div className="pv-best-price-value">{fmt(bestCoverage.totalPrice)}</div>
                  <div className="pv-best-price-label">Gesamt</div>
                </div>
              </div>
            </div>
          )}
          {bestPrice && (
            <div className="pv-best-card pv-best-card-blue">
              <div className="pv-best-card-eyebrow pv-eyebrow-blue">💰 GÜNSTIGSTER PREIS</div>
              <div className="pv-best-card-body">
                <div className="pv-best-card-info">
                  <div className="pv-best-store-name">{bestPrice.name}</div>
                  <div className="pv-best-store-meta">
                    {bestPrice.articleCount}/{bestPrice.totalArticles} Artikel · {bestPrice.coverage}%
                  </div>
                  <div className="pv-best-checkmarks pv-checkmarks-blue">
                    <div>✓ Günstigster Gesamtpreis</div>
                    <div>✓ Weniger Artikel, dafür billig</div>
                  </div>
                </div>
                <div className="pv-best-price-box pv-price-box-blue">
                  <div className="pv-best-price-value">{fmt(bestPrice.totalPrice)}</div>
                  <div className="pv-best-price-label">Gesamt</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Section B: 2-Laden Kombi ── */}
      {twoStoreCombo && twoStoreCombo.savings >= 0.10 && (
        <div className="pv-combo-card">
          <div className="pv-combo-title">📍 Noch mehr sparen mit 2 Läden</div>
          <div className="pv-combo-desc">
            Gleiche {twoStoreCombo.store1RemainingCount + 1} Artikel,
            aber <strong>{twoStoreCombo.swapArticle}</strong> bei {twoStoreCombo.store2} statt {twoStoreCombo.store1}:
          </div>
          <div className="pv-combo-breakdown">
            <div className="pv-combo-row">
              <span>{twoStoreCombo.store1RemainingCount} Artikel bei {twoStoreCombo.store1}</span>
              <span className="pv-mono">{fmt(twoStoreCombo.store1Remaining)}</span>
            </div>
            <div className="pv-combo-row pv-combo-swap-row">
              <span>🛒 {twoStoreCombo.swapArticle} bei {twoStoreCombo.store2}</span>
              <span className="pv-mono pv-text-green">{fmt(twoStoreCombo.store2Price)}</span>
            </div>
            <div className="pv-combo-divider" />
            <div className="pv-combo-row pv-combo-total-row">
              <span>Gesamt (2 Läden)</span>
              <span className="pv-mono pv-text-green">{fmt(twoStoreCombo.totalPrice)}</span>
            </div>
          </div>
          <div className="pv-combo-fazit">
            Statt {fmt(refStore?.totalPrice)} nur bei {refStore?.name} →{' '}
            <strong>{fmt(twoStoreCombo.savings)} gespart</strong> durch {twoStoreCombo.store2}-Stopp
          </div>
        </div>
      )}

      {twoStoreCombo && twoStoreCombo.savings < 0.10 && (
        <div className="pv-combo-card pv-combo-card-muted">
          <div className="pv-combo-title">📍 2-Laden Kombi</div>
          <div>Kein relevanter Preisvorteil durch zweiten Laden.</div>
        </div>
      )}
    </div>
  );
};

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

const Preisvergleich = ({ list, onClose }) => {
  const { token } = useToken();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('laden');
  const [expandedStores, setExpandedStores] = useState(new Set());

  useEffect(() => {
    if (USE_MOCK) {
      setTimeout(() => {
        setData(MOCK_DATA);
        setExpandedStores(new Set([MOCK_DATA.bestStore?.key]));
        setLoading(false);
      }, 1200);
      return;
    }
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const items = (list.items || [])
        .filter(i => !i.checked)
        .map(i => ({ name: i.name }));

      if (items.length === 0) {
        setError('Keine offenen Artikel in der Liste.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/n8n/preisvergleich`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          mode: 'prod',
          listName: list.name,
          listId: list.id,
          items
        })
      });

      const json = await response.json();

      if (json.success && json.stores) {
        setData(json);
        if (json.bestStore?.key) {
          setExpandedStores(new Set([json.bestStore.key]));
        }
      } else {
        setError(json.error || 'Preisvergleich fehlgeschlagen.');
      }
    } catch (err) {
      setError('Verbindungsfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleStore = (storeKey) => {
    setExpandedStores(prev => {
      const next = new Set(prev);
      if (next.has(storeKey)) next.delete(storeKey);
      else next.add(storeKey);
      return next;
    });
  };

  const VIEWS = [
    { id: 'laden',   label: '🏪 Laden'    },
    { id: 'tabelle', label: '📊 Tabelle'  },
    { id: 'kombi',   label: '⚡ Kombi'    },
  ];

  return (
    <div className="pv-overlay" role="dialog" aria-modal="true">
      <div className="pv-container">

        {/* ── Header ── */}
        <div className="pv-header">
          <button className="pv-back-btn" onClick={onClose} aria-label="Zurück">
            ← Zurück
          </button>
          <div>
            <h2 className="pv-title">🔍 Preisvergleich</h2>
            <div className="pv-subtitle">{list.name}</div>
          </div>
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div className="pv-loading">
            <div className="pv-spinner" />
            <div className="pv-loading-text">Preise werden verglichen…</div>
            <div className="pv-loading-hint">Das kann 3–8 Sekunden dauern</div>
          </div>
        )}

        {/* ── Error ── */}
        {error && !loading && (
          <div className="pv-error-box">
            <div>❌ {error}</div>
            <button className="pv-retry-btn" onClick={() => { setLoading(true); setError(null); fetchData(); }}>
              Erneut versuchen
            </button>
          </div>
        )}

        {/* ── Inhalt ── */}
        {data && !loading && (
          <div className="pv-content">

            {/* KPI Bar */}
            {data.isSameStore ? (
              <div className="pv-kpi-bar">
                <div className="pv-kpi-card pv-kpi-card-full">
                  <div className="pv-kpi-eyebrow">🏆 Bester Laden</div>
                  <div className="pv-kpi-store-name">{data.bestStore?.name}</div>
                  <div className="pv-kpi-value">{fmt(data.bestStore?.totalPrice)}</div>
                  <div className="pv-kpi-savings">
                    {data.bestStore?.articleCount}/{data.bestStore?.totalArticles} Artikel · {data.bestStore?.coverage}%
                  </div>
                </div>
              </div>
            ) : (
              <div className="pv-kpi-bar">
                <div className="pv-kpi-card">
                  <div className="pv-kpi-eyebrow">🛒 Beste Verfügbarkeit</div>
                  <div className="pv-kpi-store-name">{data.bestCoverage?.name}</div>
                  <div className="pv-kpi-value">{fmt(data.bestCoverage?.totalPrice)}</div>
                  <div className="pv-kpi-savings">
                    {data.bestCoverage?.articleCount}/{data.bestCoverage?.totalArticles} Artikel
                  </div>
                </div>
                <div className="pv-kpi-card pv-kpi-card-blue">
                  <div className="pv-kpi-eyebrow">💰 Günstigster</div>
                  <div className="pv-kpi-store-name">{data.bestPrice?.name}</div>
                  <div className="pv-kpi-value pv-kpi-value-blue">{fmt(data.bestPrice?.totalPrice)}</div>
                  <div className="pv-kpi-savings pv-kpi-savings-blue">
                    {data.bestPrice?.articleCount}/{data.bestPrice?.totalArticles} Artikel
                  </div>
                </div>
              </div>
            )}

            {/* View-Toggle */}
            <div className="pv-toggle" role="tablist">
              {VIEWS.map(v => (
                <button
                  key={v.id}
                  role="tab"
                  aria-selected={activeView === v.id}
                  className={`pv-toggle-btn${activeView === v.id ? ' pv-toggle-active' : ''}`}
                  onClick={() => setActiveView(v.id)}
                >
                  {v.label}
                </button>
              ))}
            </div>

            {/* Views */}
            {data.stores.length === 0 ? (
              <div className="pv-empty">Keine Preisdaten gefunden.</div>
            ) : (
              <div role="tabpanel">
                {activeView === 'laden'   && <LadenView   data={data} expandedStores={expandedStores} toggleStore={toggleStore} />}
                {activeView === 'tabelle' && <TabelleView data={data} />}
                {activeView === 'kombi'   && <KombiView   data={data} />}
              </div>
            )}

            {/* Auswertung */}
            <Auswertung data={data} />

            {/* Nicht gefunden */}
            {data.notFound && data.notFound.length > 0 && (
              <div className="pv-not-found">
                <span className="pv-not-found-label">❌ Nicht gefunden:</span>{' '}
                {data.notFound.join(', ')}
              </div>
            )}

            {/* Stats */}
            <div className="pv-stats-row">
              <span>{data.artikelGefunden}/{data.artikelGesucht} Artikel gefunden</span>
              {data.dbTreffer != null && <span>{data.dbTreffer} DB-Treffer</span>}
            </div>

            {/* Footer */}
            <div className="pv-footer">
              Preisdaten ohne Gewähr · {new Date().toLocaleDateString('de-DE')} · planivio.de
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default Preisvergleich;
